import type { HocuspocusProvider } from "@hocuspocus/provider";
import {
  type Accessor,
  For,
  type JSX,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  splitProps,
} from "solid-js";
import type * as Y from "yjs";

import styles from "~/components/post-editing/common.module.scss";
import { generateColor } from "~/components/post-editing/utils";

interface CollaborationWrapperProps
  extends JSX.InputHTMLAttributes<HTMLDivElement> {
  readonly outline?: string;
  readonly userList: JSX.Element;
  readonly class?: string;
}

export function CollaborationWrapper(props: CollaborationWrapperProps) {
  const [outlineProps, childrenProps, otherProps] = splitProps(
    props,
    ["outline"],
    ["children", "userList"],
  );

  return (
    <div
      {...otherProps}
      classList={{
        [props.class ?? ""]: true,
        ...props.classList,
        [styles["collab-wrapper"]]: true,
      }}
      style={{ "--collab-color": outlineProps.outline ?? "transparent" }}
    >
      {childrenProps.children}
      {childrenProps.userList}
    </div>
  );
}

export function useMapCollaboration<T = string>(
  props: UseCollaborationProps<Record<string, T>>,
) {
  const fragment = createMemo(() =>
    props.collaborationProvider?.document.getMap<T>(props.collaborationField),
  );

  const { value, onFocus, onBlur, userList, outlineColor } = useCollaboration(
    props,
    () => fragment(),
  );

  const onChange = (value: Record<string, T>) => {
    const stableFragment = fragment();
    if (!stableFragment) {
      return;
    }

    stableFragment.doc?.transact(() => {
      stableFragment.clear();

      for (const [k, v] of Object.entries(value)) {
        stableFragment.set(k, v);
      }
    });
  };

  return { value, onFocus, onBlur, userList, outlineColor, onChange };
}

export function useArrayCollaboration<T = string>(
  props: UseCollaborationProps<T[]>,
) {
  const fragment = createMemo(() =>
    props.collaborationProvider?.document.getArray<T>(props.collaborationField),
  );

  const { value, onFocus, onBlur, userList, outlineColor } = useCollaboration(
    props,
    () => fragment(),
  );

  const onChange = (value: T[]) => {
    const stableFragment = fragment();
    if (!stableFragment) {
      return;
    }

    stableFragment.doc?.transact(() => {
      stableFragment.delete(0, stableFragment.length);
      stableFragment.push(value);
    });
  };

  return { value, onFocus, onBlur, userList, outlineColor, onChange };
}

export function useTextCollaboration(props: UseCollaborationProps<string>) {
  const fragment = createMemo(() =>
    props.collaborationProvider?.document.getText(props.collaborationField),
  );

  const { value, onFocus, onBlur, userList, outlineColor } = useCollaboration(
    props,
    () => fragment(),
  );

  const onChange = (value: string) => {
    const stableFragment = fragment();
    if (!stableFragment) {
      return;
    }

    stableFragment.doc?.transact(() => {
      stableFragment.delete(0, stableFragment.length);
      stableFragment.insert(0, value);
    });
  };

  return { value, onFocus, onBlur, userList, outlineColor, onChange };
}

export interface UseCollaborationProps<T> {
  readonly initialValue?: T;
  readonly collaborationProvider?: HocuspocusProvider;
  readonly collaborationField?: string;
  readonly class?: string;
  readonly classList?: Record<string, boolean>;
  readonly onRemoteChange?: (newValue: T) => void;
}

function useCollaboration<T>(
  props: UseCollaborationProps<T>,
  fragment?: Accessor<any>,
) {
  const [states, setStates] = createSignal<any[]>([]);

  createEffect(() => {
    if (!props.collaborationProvider) {
      return;
    }

    const getUsers = ({ states }: { states: any[] }) => {
      setStates(
        states.filter(
          (state) =>
            state.atField === props.collaborationField &&
            state.clientId !== props.collaborationProvider?.awareness?.clientID,
        ),
      );
    };

    props.collaborationProvider.on("awarenessUpdate", getUsers);

    onCleanup(() => {
      props.collaborationProvider?.off("awarenessUpdate", getUsers);
    });
  });

  const observer: (a: Y.YEvent<Y.AbstractType<any>>, b: Y.Transaction) => void =
    (event) => {
      const newValue = event.target.toJSON();
      setValue(newValue);
      props.onRemoteChange?.(newValue);
    };

  createEffect(() => {
    const stableFragment = fragment?.();
    if (!stableFragment) {
      return;
    }

    stableFragment.observe(observer);

    onCleanup(() => {
      stableFragment.unobserve(observer);
    });
  });

  const [value, setValue] = createSignal(props.initialValue);

  const onFocus = () => {
    if (!props.collaborationProvider) {
      return;
    }

    props.collaborationProvider.setAwarenessField(
      "atField",
      props.collaborationField,
    );
  };

  const onBlur = () => {
    if (!props.collaborationProvider) {
      return;
    }

    props.collaborationProvider.setAwarenessField("atField", null);
  };

  const userList = () => (
    <div class={styles["user-list"]}>
      <For each={states()}>
        {(state) => {
          const { color, textColor } = generateColor(state.user.id);
          return (
            <span
              style={{
                "background-color": color,
                color: textColor,
              }}
            >
              {state.user.name}
            </span>
          );
        }}
      </For>
    </div>
  );

  const outlineColor = () =>
    states().length > 0
      ? generateColor(states()[0]?.user?.id).color
      : undefined;

  return { value, onFocus, onBlur, userList, outlineColor };
}
